import { useEffect, useRef, useState } from 'react';

export default function QRScanner({ onScan, onClose }) {
  const html5QrRef = useRef(null);
  const [state, setState] = useState('idle'); // idle | starting | scanning | error | manual
  const [errorMsg, setErrorMsg] = useState('');
  const [manualUrl, setManualUrl] = useState('');

  useEffect(() => {
    return () => {
      if (html5QrRef.current) {
        html5QrRef.current.stop().catch(() => {}).finally(() => {
          html5QrRef.current = null;
        });
      }
    };
  }, []);

  const startCamera = async () => {
    // Nejdřív nastavíme 'starting' – video kontejner se zobrazí,
    // aby knihovna mohla správně zjistit rozměry
    setState('starting');
    setErrorMsg('');

    // Krátká prodleva aby React přerendoval a div byl v DOM před init
    await new Promise(r => setTimeout(r, 80));

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('qr-video-container');
      html5QrRef.current = scanner;

      // Preferuj zadní kameru
      let cameraId = { facingMode: 'environment' };
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras && cameras.length > 0) {
          const back = cameras.find(c => /back|rear|environment/i.test(c.label));
          cameraId = (back || cameras[cameras.length - 1]).id;
        }
      } catch { /* fallback na facingMode */ }

      await scanner.start(
        cameraId,
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        (decoded) => {
          scanner.stop().catch(() => {});
          html5QrRef.current = null;
          onScan(decoded);
        },
        () => { /* ignoruj chyby snímků */ }
      );

      setState('scanning');
    } catch (err) {
      console.error('Kamera selhala:', err);
      const msg = String(err?.message || err);
      if (/permission|denied|notallowed/i.test(msg)) {
        setErrorMsg('Přístup ke kameře byl odmítnut. Povolte kameru v nastavení prohlížeče a zkuste znovu.');
      } else if (/notfound|no.*camera/i.test(msg)) {
        setErrorMsg('Kamera nenalezena. Zkuste zadat URL ručně.');
      } else {
        setErrorMsg('Kameru se nepodařilo spustit. Zkuste zadat URL ručně.');
      }
      if (html5QrRef.current) { html5QrRef.current.stop().catch(() => {}); html5QrRef.current = null; }
      setState('error');
    }
  };

  const stopCamera = () => {
    if (html5QrRef.current) {
      html5QrRef.current.stop().catch(() => {});
      html5QrRef.current = null;
    }
    setState('idle');
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const trimmed = manualUrl.trim();
    if (!trimmed) return;
    onScan(trimmed);
  };

  const showVideo = state === 'starting' || state === 'scanning';

  return (
    <div>

      {/* ── Idle ── */}
      {state === 'idle' && (
        <div className="text-center" style={{ padding: '16px 0' }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>📷</div>
          <p className="text-muted mb-16" style={{ fontSize: '0.95rem' }}>
            Namiřte kameru na QR kód u vchodu zóny
          </p>
          <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={startCamera}>
            Spustit kameru
          </button>
          <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => setState('manual')}>
            Zadat URL ručně
          </button>
        </div>
      )}

      {/* ── Error ── */}
      {state === 'error' && (
        <div className="text-center" style={{ padding: '8px 0' }}>
          <div className="alert alert-warning mb-16" style={{ textAlign: 'left' }}>{errorMsg}</div>
          <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={startCamera}>
            Zkusit znovu
          </button>
          <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => setState('manual')}>
            Zadat URL ručně
          </button>
        </div>
      )}

      {/* ── Video kontejner – vždy v DOM při starting/scanning ── */}
      {showVideo && (
        <div style={{ position: 'relative' }}>
          {/* Spinner overlay jen při starting */}
          {state === 'starting' && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 2,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.85)', borderRadius: 8,
              minHeight: 200,
            }}>
              <div className="spinner" style={{ marginBottom: 10 }} />
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>Spouštím kameru…</p>
            </div>
          )}

          {/* Instrukce nad kamerou */}
          {state === 'scanning' && (
            <p style={{
              textAlign: 'center', fontSize: '0.85rem', color: '#555',
              marginBottom: 8, fontWeight: 500,
            }}>
              📷 Namiřte na QR kód – skenování probíhá automaticky
            </p>
          )}

          {/* Sem html5-qrcode vykreslí video */}
          <div id="qr-video-container" style={{ width: '100%', borderRadius: 8, overflow: 'hidden' }} />

          {state === 'scanning' && (
            <div className="mt-12 text-center">
              <button className="btn btn-outline btn-sm" onClick={() => { stopCamera(); setState('manual'); }}>
                Zadat URL ručně
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Manual ── */}
      {state === 'manual' && (
        <div>
          <h3 className="mb-16">Zadat URL zóny ručně</h3>
          <p className="text-muted mb-16" style={{ fontSize: '0.9rem' }}>
            Opište krátkou adresu z QR kódu, např.:<br />
            <code style={{ fontSize: '0.85rem' }}>jablko-hora-auto</code>
          </p>
          <form onSubmit={handleManualSubmit}>
            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="slovo-slovo-slovo"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div className="flex gap-8">
              <button type="submit" className="btn btn-primary flex-1">Potvrdit</button>
              <button type="button" className="btn btn-outline" onClick={() => setState('idle')}>← Kamera</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Zavřít ── */}
      {onClose && (
        <div className="mt-16">
          <button className="btn btn-outline btn-full" onClick={() => { stopCamera(); onClose(); }}>
            Zrušit
          </button>
        </div>
      )}
    </div>
  );
}
